import { useMutation } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../api/index';
import { Button } from '../components/Button';
import { getPasswordStrengthMessage, validateEmail, validatePassword } from '../lib/validation';
import { useAuthStore } from '../store/auth';

type Step = 1 | 2 | 3

export function RegisterPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const setAuth = useAuthStore((s) => s.setAuth)

  const [step, setStep] = useState<Step>(1)
  const [agencyName, setAgencyName] = useState('')
  const [licenseNumber, setLicenseNumber] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '')

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [canResendEmail, setCanResendEmail] = useState(false)
  const [resendTimeRemaining, setResendTimeRemaining] = useState(0)

  const registerMutation = useMutation({
    mutationFn: () =>
      authAPI.register({
        agency_name: agencyName,
        license_number: licenseNumber,
        full_name: fullName,
        email,
        password,
        referral_code: referralCode || undefined,
      }),
    onSuccess: (res: any) => {
      // Registration successful but email not verified yet
      // Show verification pending page
      setStep(3)
      setCanResendEmail(false)
      setResendTimeRemaining(600) // 10 minutes in seconds
      toast.success('Account created! Check your email to verify.')
    },
    onError: (error: any) => {
      const msg = error.response?.data?.error
      if (msg?.includes('email')) {
        toast.error('Email already registered')
      } else if (msg?.includes('license')) {
        toast.error('License number already registered')
      } else {
        toast.error(msg || 'Registration failed')
      }
    },
  })

  const resendVerificationMutation = useMutation({
    mutationFn: () => authAPI.resendVerificationEmail(email),
    onSuccess: () => {
      setCanResendEmail(false)
      setResendTimeRemaining(600) // 10 minutes
      toast.success('Verification email sent! Check your inbox.')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to resend email')
    },
  })

  // Timer for resend button
  useEffect(() => {
    if (resendTimeRemaining <= 0) {
      setCanResendEmail(true)
      return
    }

    const timer = setInterval(() => {
      setResendTimeRemaining((prev) => prev - 1)
    }, 1000)

    return () => clearInterval(timer)
  }, [resendTimeRemaining])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (step === 1) {
      if (!agencyName) {
        toast.error('Please fill in agency name')
        return
      }
      setStep(2)
      return
    }

    if (!fullName || !email || !password || !confirmPassword) {
      toast.error('Please fill in all fields')
      return
    }

    if (!validateEmail(email)) {
      toast.error('Please enter a valid email address')
      return
    }

    if (!validatePassword(password)) {
      const requirements = getPasswordStrengthMessage()
      toast.error(`Password must contain: ${requirements.join(', ')}`)
      return
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    registerMutation.mutate()
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-8">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <img src="/image.png" alt="RTO Logo" className="h-24 rounded-lg object-cover" />
        </div>

        {/* Step indicator - only show for steps 1 and 2 */}
        {step !== 3 && (
          <div className="mb-8">
            <div className="flex items-center gap-4">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                  step >= 1 ? 'bg-saffron text-white' : 'bg-gray-200 text-gray-700'
                }`}
              >
                1
              </div>
              <div className={`flex-1 h-1 ${step >= 2 ? 'bg-saffron' : 'bg-gray-200'}`} />
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                  step >= 2 ? 'bg-saffron text-white' : 'bg-gray-200 text-gray-700'
                }`}
              >
                2
              </div>
            </div>
            <p className="text-center text-sm text-gray-600 mt-4">
              {step === 1 ? 'Step 1 — Agency Details' : 'Step 2 — Your Account'}
            </p>
          </div>
        )}

        {step === 3 ? (
          <>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Verify Your Email
            </h1>
            <p className="text-gray-600 mb-6">
              We've sent a verification link to {email}
            </p>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex gap-3">
              <Check className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" />
              <div className="text-sm text-green-800">
                <p className="font-medium mb-1">Account created successfully!</p>
                <p>
                  Check your email for a verification link and click it to complete
                  registration.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <button
                type="button"
                onClick={() => window.location.href = 'https://mail.google.com'}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700"
              >
                Open Email
              </button>

              <Button
                type="button"
                variant="secondary"
                className="w-full"
                onClick={() => resendVerificationMutation.mutate()}
                disabled={!canResendEmail || resendVerificationMutation.isPending}
                isLoading={resendVerificationMutation.isPending}
              >
                {resendTimeRemaining > 0
                  ? `Resend Email (${Math.floor(resendTimeRemaining / 60)}:${String(resendTimeRemaining % 60).padStart(2, '0')})`
                  : 'Resend Verification Email'}
              </Button>

              <div className="text-center text-sm text-gray-600">
                <p className="mb-3">Already clicked the link?</p>
                <Link to="/login" className="text-saffron hover:text-orange-600 font-medium">
                  Go to Sign In
                </Link>
              </div>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Create Your Account
            </h1>
            <p className="text-gray-600 mb-6">
              {step === 1 ? 'Tell us about your agency' : 'Create your account credentials'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {step === 1 && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Agency Name *
                    </label>
                    <input
                      type="text"
                      value={agencyName}
                      onChange={(e) => setAgencyName(e.target.value)}
                      placeholder="Your RTO Agency"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-saffron"
                      disabled={registerMutation.isPending}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      License Number (Optional)
                    </label>
                    <input
                      type="text"
                      value={licenseNumber}
                      onChange={(e) => setLicenseNumber(e.target.value)}
                      placeholder="Your RTO license"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-saffron"
                      disabled={registerMutation.isPending}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="City (optional)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-saffron"
                      disabled={registerMutation.isPending}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      placeholder="State (optional)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-saffron"
                      disabled={registerMutation.isPending}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Referral Code
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={referralCode}
                        onChange={(e) => setReferralCode(e.target.value)}
                        placeholder="Optional"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-saffron"
                        disabled={registerMutation.isPending}
                      />
                      {referralCode && (
                        <Check className="absolute right-3 top-3 w-5 h-5 text-sage" />
                      )}
                    </div>
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Your full name"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-saffron"
                      disabled={registerMutation.isPending}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-saffron"
                      disabled={registerMutation.isPending}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password *
                    </label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Create password"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-saffron"
                      disabled={registerMutation.isPending}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Must contain: 8+ chars, 1 uppercase, 1 lowercase, 1 digit, 1 special char
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm password"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-saffron"
                      disabled={registerMutation.isPending}
                    />
                  </div>
                </>
              )}

              <div className="flex gap-3 pt-4">
                {step === 2 && (
                  <Button
                    type="button"
                    variant="secondary"
                    className="flex-1"
                    onClick={() => setStep(1)}
                    disabled={registerMutation.isPending}
                  >
                    Back
                  </Button>
                )}
                <Button
                  type="submit"
                  className={step === 2 ? 'flex-1' : 'w-full'}
                  isLoading={registerMutation.isPending}
                  disabled={registerMutation.isPending}
                >
                  {step === 1 ? 'Next' : 'Create Account'}
                </Button>
              </div>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-center text-gray-600 text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-saffron hover:text-orange-600 font-medium">
                  Sign in
                </Link>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
